import React, { Component } from "react";
import { withRouter } from "react-router-dom";

import client from "@culturehq/client";
import { Form, Grid, Loader, SelectField, StringField, SubmitButton, Success } from "@culturehq/components";

import { useFieldsLabels } from "app/hooks";
import { errorService, t, Trans } from "app/utils";

import LazyEditor from "components/editor/LazyEditor";
import Announcement from "components/featured/Announcement";
import ErrorMessage from "components/common/ErrorMessage";
import SelectTags from "components/post/SelectTags";
import { getCategoryLabel, getIcon } from "constants/tagsTypes";

class WidgetEditContext extends Component {
  editorRef = React.createRef();

  state = {
    widget: { title: "", body: "", active: true },
    preview: { title: "", body: "" },
    error: false,
    success: false,
    loading: true,
    displayOn: "for_you",
    selectedOptions: [],
    active: true
  };

  async componentDidMount() {
    this.componentIsMounted = true;

    const { labels, match: { params: { id: announcementId } } } = this.props;
    let mutation = {};

    if (announcementId) {
      try {
        const { announcement: widget } = await client.getAnnouncement({ announcementId });
        const selectedOptions = widget.announcementTags?.map(({ entity, entityType }) => (
          {
            category: getCategoryLabel(entityType, labels),
            value: entity.id,
            label: entity.name,
            categoryIcon: getIcon(entityType)
          }
        )) || [];
        mutation = {
          widget,
          preview: widget,
          displayOn: widget.displayOn,
          selectedOptions,
          active: widget.active
        };
      } catch (error) {
        errorService.error(error);
        mutation = { error };
      }
    }

    this.setState({ ...mutation, loading: false });
  }

  componentWillUnmount() {
    this.componentIsMounted = false;
  }

  handleTitleChange = title => {
    this.setState(({ preview }) => ({ preview: { ...preview, title } }));
  };

  handleBodyChange = body => {
    this.setState(({ preview }) => ({ preview: { ...preview, body } }));
  };

  handleSubmit = async values => {
    const { selectedOptions } = this.state;
    this.setState({ error: false, success: false });

    if (values.displayOn === "explore" && selectedOptions.length === 0) {
      this.setState({ success: false, error: t("You need to select at least one tag") });
      return;
    }

    const { widget } = this.state;
    const body = await this.editorRef.current.serialize();

    try {
      if (widget.id) {
        await client.updateAnnouncement(
          { ...values, body, announcementId: widget.id, allOptions: selectedOptions }
        );
      } else {
        await client.createAnnouncement({ ...values, body, allOptions: selectedOptions });
      }

      this.setState({ success: true, error: false });
    } catch (error) {
      this.setState({ success: false, error });
    }
  };

  handleActiveChange = value => {
    this.setState({ active: value });
  };

  handleDisplayOn = value => {
    this.setState({ displayOn: value });
  };

  handleSelectedTags = (updatedOptions, _) => {
    const { preview } = this.state;
    this.setState({
      selectedOptions: updatedOptions,
      preview: { ...preview, announcementTags: updatedOptions }
    });
  };

  render() {
    const {
      displayOn,
      error,
      success,
      loading,
      preview,
      selectedOptions,
      widget,
      active: activeWidget
    } = this.state;
    const { title, body, active } = widget;

    return (
      <Loader loading={loading}>
        <Grid className="Widget">
          <Grid.Item md={6}>
            {error && <ErrorMessage error={error} />}

            {success && (
              <Success><Trans>Success!</Trans></Success>
            )}

            <Form onSubmit={this.handleSubmit} initialValues={{ title, active }}>
              <StringField name="title" required onChange={this.handleTitleChange}>
                <Trans>Title</Trans>
              </StringField>
              <div className="chq-ffd">
                <span className="chq-ffd--lb">
                  <Trans>Message</Trans>
                </span>
                <LazyEditor
                  ref={this.editorRef}
                  initialValue={body}
                  onChange={this.handleBodyChange}
                />
              </div>
              <SelectField
                name="active"
                options={[
                  { label: t("Enabled — display widget."), value: true },
                  { label: t("Disabled - don't display widget."), value: false }
                ]}
                onChange={this.handleActiveChange}
                required
              >
                <Trans>Enabled</Trans>
              </SelectField>
              {activeWidget && (
                <>
                  <SelectField
                    name="displayOn"
                    options={[
                      { label: t("For you feed"), value: "for_you" },
                      { label: t("Explore page"), value: "explore" }
                    ]}
                    onChange={this.handleDisplayOn}
                    value={displayOn}
                    required
                  >
                    <Trans>Display on</Trans>
                  </SelectField>
                  {displayOn === "explore" && (
                    <div className="tags-container">
                      <span className="chq-ffd--lb"><Trans>Select Tags</Trans></span>
                      <SelectTags
                        ariaLabel={t("Select tags in which the widget will be displayed")}
                        normalDesign={false}
                        placeholder={t("Select tags in which the widget will be displayed")}
                        selectedOptions={selectedOptions}
                        onShareWithChanged={this.handleSelectedTags}
                        required
                      />
                    </div>
                  )}
                </>
              )}
              {!widget.id && activeWidget && <p className="Widget__explanation"><Trans>* Enabling a new widget will create a feed item.</Trans></p>}
              <SubmitButton>
                {submitting => (submitting ? t("Submitting...") : t("Save Widget"))}
              </SubmitButton>
            </Form>
          </Grid.Item>
          <Grid.Item md={6} className="Featured chq-widget-preview">
            <h2 className="text-center">
              <Trans>Widget Preview</Trans>
            </h2>
            <div className="col-xs-10 col-xs-offset-1">
              <Announcement widget={preview} isPreview selectedOptions={selectedOptions} />
            </div>
          </Grid.Item>
        </Grid>
      </Loader>
    );
  }
}

const WidgetEdit = props => {
  const labels = useFieldsLabels();

  return (
    <WidgetEditContext
      labels={labels}
      {...props}
    />
  );
};

export default withRouter(WidgetEdit);
